import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Plus, Check, X } from "lucide-react";
import { API, api, getLocationId, errMsg } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const SUGGEST = {
  inventory: ["What inventory items need attention?", "What should I worry about in inventory?"],
  sales: ["What looks abnormal in recent sales?", "Are our discounts getting worse?"],
  tasks: ["What's overdue and who owns it?"], log: ["What problems keep appearing in manager logs?"],
  today: ["What needs attention before service tonight?", "What should I focus on today?"],
  dashboard: ["How did we perform yesterday?", "Give me an ownership update for this week."],
  onboarding: ["What should I enter for my labor target?", "Which daily tools should a full-service restaurant turn on?"],
};
export const openAsk = (prompt = "") => window.dispatchEvent(new CustomEvent("open-ask", { detail: { prompt } }));

function parseAction(text) {
  const m = text.match(/```action\s*([\s\S]*?)```/);
  if (!m) return [text, null];
  try { return [text.replace(m[0], "").trim(), JSON.parse(m[1])]; } catch { return [text.replace(m[0], "").trim(), null]; }
}
const TAG_RX = /(\*\*.+?\*\*|\[(?:Actual|Estimate|Recommendation|Missing)\])/gi;
const LEAD_RX = /^(\s*[-•]?\s*)(Actual|Estimate|Recommendation|Missing)(?=:|\s*$)/i;
const TagPill = ({ t }) => <span className={`ask-tag ask-${t.toLowerCase()}`}>{t.toLowerCase()}</span>;
const inline = (s) => s.split(TAG_RX).map((part, j) => {
  if (/^\*\*.+\*\*$/.test(part)) return <b key={j}>{part.slice(2, -2)}</b>;
  const m = part.match(/^\[(Actual|Estimate|Recommendation|Missing)\]$/i);
  return m ? <TagPill key={j} t={m[1]} /> : part;
});
const Tagged = ({ text }) => text.split("\n").map((ln, i) => {
  const lead = ln.match(LEAD_RX);
  return <p key={i} className="min-h-[0.5rem]">{lead ? <>{lead[1]}<TagPill t={lead[2]} />{inline(ln.slice(lead[0].length))}</> : inline(ln)}</p>;
});

function ActionCard({ a }) {
  const [state, setState] = useState("idle");
  const run = async () => {
    setState("busy");
    try {
      if (a.type === "create_task") await api.post("/tasks", { title: a.title, priority: a.priority || "Medium", due_date: a.due_date || null, notes: a.notes || "Suggested by Ask 86'd", status: "Open" });
      else await api.post("/manager-logs", { date: new Date().toISOString().slice(0, 10), service_notes: a.service_notes || "", tomorrow: a.tomorrow || "" });
      setState("done"); toast.success(a.type === "create_task" ? "Task created" : "Manager log draft saved");
    } catch (e) { toast.error(errMsg(e)); setState("idle"); }
  };
  if (state === "dismissed") return null;
  return (
    <div className="mt-2 rounded-lg border border-[#9E2A2B]/30 bg-[#FBF1EC] p-3 text-sm" data-testid="ask-action">
      <div className="font-medium">{a.type === "create_task" ? `Create task: ${a.title}` : "Save a manager log draft"}</div>
      {a.due_date && <div className="text-xs text-stone-600">Due {a.due_date} · {a.priority}</div>}
      {state === "done" ? <div className="text-emerald-800 mt-2 flex gap-1 items-center"><Check className="h-4 w-4" />Done</div> :
        <div className="flex gap-2 mt-2"><Button size="sm" onClick={run} disabled={state === "busy"} data-testid="ask-action-confirm">{state === "busy" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}Confirm</Button>
          <Button size="sm" variant="ghost" onClick={() => setState("dismissed")} data-testid="ask-action-dismiss"><X className="h-3 w-3 mr-1" />No thanks</Button></div>}
    </div>
  );
}

export default function AskDrawer() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(null);
  const end = useRef(null);
  const loc = useLocation();
  const page = loc.pathname.replace(/^\/(app\/)?/, "") || "dashboard";
  const key = page.split("/")[0];
  useEffect(() => { const h = (e) => { setOpen(true); if (e.detail?.prompt) setQ(e.detail.prompt); }; window.addEventListener("open-ask", h); return () => window.removeEventListener("open-ask", h); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async (text) => {
    const m = (text ?? q).trim(); if (!m || busy) return;
    setQ(""); setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/ask`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", "X-Requested-With": "86d", ...(getLocationId() ? { "X-Location-Id": getLocationId() } : {}) },
        body: JSON.stringify({ message: m, page, session_id: sid.current }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(typeof j.detail === "string" ? j.detail : "Ask 86'd couldn't answer that"); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { done, value } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const ev = (p.match(/^event: (.+)$/m) || [])[1]; const data = JSON.parse((p.match(/^data: (.+)$/m) || [, "{}"])[1]);
          if (ev === "session") sid.current = data.session_id;
          else if (ev === "error") throw new Error(data.error);
          else if (data.t) setMsgs((x) => { const c = [...x]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + data.t }; return c; });
        }
      }
    } catch (e) {
      setMsgs((x) => { const c = [...x]; c[c.length - 1] = { role: "assistant", content: e.message, error: true }; return c; });
    } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="h-10 w-10 sm:w-auto sm:px-3 shrink-0 rounded-full bg-[#1C1917] text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-stone-800" data-testid="ask-open-btn">
        <Sparkles className="h-4 w-4" /><span className="hidden sm:inline">Ask 86'd</span></button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-[#FBF9F5] w-full sm:max-w-md flex flex-col p-0" data-testid="ask-drawer">
          <SheetHeader className="p-5 border-b border-[#E6E1D7] text-left">
            <SheetTitle className="font-display text-2xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#9E2A2B]" />Ask 86'd</SheetTitle>
            <SheetDescription>Ask your restaurant a question. Answers use your 86'd data and flag anything that's missing.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm" data-testid="ask-messages">
            {!msgs.length && <div className="space-y-2">{(SUGGEST[key] || SUGGEST.dashboard).concat(["What should I talk about at tomorrow's manager meeting?"]).map((s) => (
              <button key={s} onClick={() => send(s)} className="block w-full text-left rounded-lg border border-[#E6E1D7] bg-white p-3 hover:border-stone-400" data-testid="ask-suggestion">{s}</button>))}</div>}
            {msgs.map((m, i) => { const [txt, action] = m.role === "assistant" ? parseAction(m.content) : [m.content, null]; return (
              <div key={i} className={m.role === "user" ? "ml-8 rounded-xl bg-stone-900 text-white p-3" : `mr-4 rounded-xl bg-white border p-3 space-y-1 ${m.error ? "border-rose-300 text-rose-800" : "border-[#E6E1D7]"}`} data-testid={`ask-msg-${m.role}`}>
                {m.role === "assistant" && !m.content && busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tagged text={txt} />}
                {action && !busy && <ActionCard a={action} />}
              </div>); })}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-[#E6E1D7] flex gap-2 bg-white">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="How did we do yesterday?" className="flex-1 h-12 rounded-full border px-4 text-base" data-testid="ask-input" />
            <Button className="h-12 w-12 rounded-full" disabled={busy || !q.trim()} aria-label="Send question to Ask 86'd" data-testid="ask-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
